
import type { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response'
import type {
  TrendingData,
  TrendingDataPoint,
} from '@empathybroker/tsclient-stats/dist/models/trendingreport/trendingdata'
import type { Bubble, BubblesClusterConfig, Params } from '@empathyco/insights'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { BubblesClusterComponent } from '@empathyco/insights'
import { defineComponent } from 'vue'

import { mapActions, mapGetters } from 'vuex'

import InsightsNoResults from '../../components/insights/insights-no-results.component.vue'
import { UiTooltip } from '../../components/keen-ui'
import KpiBar from '../../components/kpi-bar.component.vue'
import { EpbButton } from '../../components/shared/epb-button'
import { EpbText } from '../../components/shared/epb-text'

import { useAppLoading } from '../../composables/use-app-loading'

import { useKpis } from '../../composables/use-kpis'
import { FeatureIdEnum, KpiEnum } from '../../enums'

import { InsightsMixin } from '../../mixins'

import { ANALYTICS_CONSTS } from '../../store/analytics/analytics.consts'

import { APP_CONSTS } from '../../store/app/app.consts'
import { getKpiTrendingCount, getKpiTrendingData } from '../../utils/kpis/trending-report'

interface Data {
  trending: TrendingData
}

interface ITrendingDataTotals {
  queryCount: number
  clickCount: number
  wishlistCount: number
  add2cartCount: number
  noResultsCount: number
}

export default defineComponent({
  name: 'Now',
  components: {
    EpbButton,
    EpbText,
    InsightsNoResults,
    KpiBar,
    UiTooltip,
  },
  mixins: [InsightsMixin],
  setup() {
    const { KpisOfTools, kpiOptions, getKpiStyle } = useKpis()
    const kpiIds = KpisOfTools[FeatureIdEnum.NOW]
    const countersIds = ['total_queries', ...kpiIds]
    const kpis = countersIds.slice(1)

    const { loading } = useAppLoading()

    return { kpiOptions, kpiIds, loading, countersIds, kpis, getKpiStyle }
  },
  data(): Record<string, any> {
    return {
      baseClass: 'epb-now',
      components: undefined,
      browserFullscreenEnabled: document.fullscreenEnabled,
      fetchDataInterval: undefined,
      messages: this.$epb.messages.insights.now,
    }
  },
  computed: {
    ...mapGetters(APP_CONSTS.moduleName, {
      fullscreen: APP_CONSTS.getters.fullscreen,
    }),
    componentsParams(): Params<BubblesClusterConfig, Bubble[]>[] {
      return this.kpis.map((kpi: KpiEnum) => this.getComponentParams(kpi))
    },
    hasNoResults(): boolean {
      return (
        !this.loading &&
        this.componentsParams.every(param => {
          return param.data.length === 0
        })
      )
    },
  },
  methods: {
    ...mapActions(ANALYTICS_CONSTS.moduleName, {
      fetchStatsReport: ANALYTICS_CONSTS.actions.fetchStatsReport,
    }),
    getLabel(counter: string): string {
      const kpiLabel = this.kpiOptions.find(kpi => kpi.id === counter)?.label

      return kpiLabel || this.messages.totalQueries
    },
    getCounterValueString(counter: string): string {
      return this.formatNumber(this.getCounterValue(counter))
    },
    formatNumber(number: number, options = {}, roundLongNumber = false): string {
      return this.$epb.utils.formatNumber(number, options, roundLongNumber)
    },
    getCounterValue(counter: string): number {
      const totals: ITrendingDataTotals = this.data.trending.totals
      if (counter === 'total_queries') {
        return (
          getKpiTrendingCount(KpiEnum.QUERIES, totals) +
          getKpiTrendingCount(KpiEnum.NO_RESULTS, totals)
        )
      }
      return getKpiTrendingCount(counter as KpiEnum, totals)
    },
    getCounterKpiData(counter: string): { id: KpiEnum; percentage: number } {
      return {
        id: counter as KpiEnum,
        percentage: this.getCounterPercentage(counter),
      }
    },
    getCounterPercentage(counter: string): number {
      const queryCount = this.getCounterValue('total_queries')
      return queryCount > 0 ? (this.getCounterValue(counter) / queryCount) * 100 : 0
    },
    getCounterPercentageString(counter: string): string {
      return this.formatNumber(
        this.getCounterPercentage(counter),
        { maximumFractionDigits: 2 },
        true,
      )
    },
    onFullscreenClick(): void {
      const { fullscreen, ...newQueryParams } = this.$route.query
      if (!this.fullscreen) {
        newQueryParams.fullscreen = 'true'
      }
      this.$router.replace({
        ...this.$route,
        path: this.$route.name,
        query: newQueryParams,
      })
    },
    getComponentParams(kpi: KpiEnum): Params<BubblesClusterConfig, Bubble[]> {
      const { count, minRadius, maxRadius } = this.getBubblesProps(kpi, kpi === KpiEnum.QUERIES)
      const data = this.getComponentData(kpi, count)

      return {
        config: {
          minRadius,
          maxRadius,
          maxOpacity: 1,
          hoverRadius: maxRadius,
          valueLabel: this.getLabel(kpi),
          transitionsDurationInMillis: 1600,
          delayTransitionInMillis: data.length ? 600 / data.length : 0,
          animated: true,
          showDefaultCursor: true,
        },
        data,
      }
    },
    getBubblesProps(
      kpi: KpiEnum,
      main: boolean,
    ): { count: number; minRadius: number; maxRadius: number } {
      const section = this.$refs.section
      if (!section) {
        return {
          count: 0,
          minRadius: 0,
          maxRadius: 0,
        }
      }
      const width = section.offsetWidth - this.$refs.counters.offsetWidth
      if (width >= 3500) {
        // 4k
        return {
          count: main ? 60 : 35,
          minRadius: main ? width / 80 : width / 100,
          maxRadius: main ? width / 40 : width / 50,
        }
      }
      if (width >= 3000) {
        // 21:9
        return {
          count: main ? 40 : 20,
          minRadius: main ? width / 70 : width / 80,
          maxRadius: main ? width / 35 : width / 40,
        }
      }
      if (width >= 1600) {
        // full hd, 2k or macbook laptop
        return {
          count: main ? 35 : 20,
          minRadius: main ? width / 68 : width / 75,
          maxRadius: main ? width / 26 : width / 36,
        }
      }
      if (width >= 1024) {
        return {
          count: main ? 25 : 15,
          minRadius: main ? width / 42 : width / 48,
          maxRadius: main ? width / 22 : width / 26,
        }
      }
      return {
        count: 15,
        minRadius: main ? width / 30 : width / 35,
        maxRadius: main ? width / 16 : width / 24,
      }
    },
    getComponentData(kpi: KpiEnum, bubblesCount: number): Bubble[] {
      if (!this.data) {
        return []
      }
      const trending: TrendingData = this.data.trending
      const kpiData = getKpiTrendingData(kpi, trending)

      const selectedKpiData = kpiData
        .sort(
          (trendingDataPoint1: TrendingDataPoint, trendingDataPoint2: TrendingDataPoint) =>
            trendingDataPoint2.count - trendingDataPoint1.count,
        )
        .slice(0, bubblesCount)
      return selectedKpiData.map((trendingDataPoint: TrendingDataPoint) => ({
        name: trendingDataPoint.query,
        value: trendingDataPoint.count,
      }))
    },
    async fetchData(): Promise<Data> {
      const trendingResponse: StatsResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.TRENDING,
        params: {},
      })
      return {
        trending: trendingResponse.data,
      }
    },
  },
  watch: {
    componentsParams: {
      handler(params: Params<BubblesClusterConfig, Bubble[]>[]): void {
        this.kpiIds.forEach((kpi: KpiEnum, index: number) => {
          this.components[index].update(params[index])
        })
      },
      deep: true,
    },
  },
  created(): void {
    this.components = [...Array.from({ length: this.kpiIds.length })].map(
      () => new BubblesClusterComponent(),
    )
  },

  mounted(): void {
    this.fetchDataInterval = setInterval(async () => {
      if (document.visibilityState === 'visible') {
        this.data = await this.fetchData()
      }
    }, 30000)
    this.components.forEach((component: BubblesClusterComponent, index: number) => {
      const kpi = this.kpiIds[index]
      const params = this.getComponentParams(kpi)
      component.init(this.$refs[`${kpi}-component`][0], params)
    })
  },
  beforeDestroy(): void {
    clearInterval(this.fetchDataInterval)
    this.components.forEach((component: BubblesClusterComponent) => component.destroy())
  },
})
